/**
 * The plain-text half of an email, derived from the HTML half.
 *
 * ## Why the console derives it at all
 *
 * Every real email is two documents: the HTML most people see, and the `text/plain` alternative
 * that a spam filter scores, a screen reader reads, a watch renders, and a corporate gateway
 * substitutes when it strips HTML outright. A message with no text part is a message that scores
 * worse and is unreadable to a slice of the people it was sent to.
 *
 * The API's `preview` route returns the rendered HTML and nothing else, so the text part does not
 * exist anywhere yet. This module builds it from the HTML the operator is looking at, which means
 * the preview is honest about the message that would actually be delivered rather than about half
 * of it. When the render step grows a text part, this becomes the fallback and the server's copy
 * wins.
 *
 * ## Why string surgery rather than the DOM
 *
 * `DOMParser` would be shorter and is available in the browser. It is not used, for two reasons.
 * The first is that this has to run in `node` under vitest, where there is no DOM, and a preview
 * rule nobody can test is a preview rule that drifts. The second matters more: parsing operator
 * HTML with the browser's own parser means constructing a live document from a string the console
 * did not write. Nothing here executes, fetches, or evaluates anything: it is regular expressions
 * over a string, start to finish.
 *
 * ## What it preserves
 *
 * Link destinations, because "click here" with the href thrown away is the exact text part that
 * makes a message look like phishing. Image alt text, for the same reason a screen reader wants
 * it. Block structure: a paragraph break where the HTML had one, a single line break where it had
 * a row or a list item, and a gap between the cells of a row so a receipt still reads as a
 * receipt.
 */

/**
 * Two private sentinels, standing in for "paragraph break" and "cell gap" while the tags are
 * being taken apart.
 *
 * They exist because the obvious approach, emitting a newline for every block boundary and then
 * collapsing runs, cannot tell the difference between the two newlines that `</p><p>` produces
 * (one boundary, wanted as a blank line) and the two that `</tr><tr>` produces (one boundary,
 * wanted as a single line break). Collapsing loses the paragraph; not collapsing double-spaces
 * the table. A distinct marker for the stronger break survives the collapse and keeps both right.
 *
 * They are Private Use Area code points rather than NUL and SOH. Two reasons, and the second is
 * the real one: `no-control-regex` refuses a control character in a regular expression, and it is
 * right to — but more importantly a control character in operator HTML is at least imaginable,
 * whereas nothing in an email template is ever legitimately a private-use code point. Either way
 * `SENTINELS` strips both out of the input before they could forge a boundary.
 */
const PARA = '\uE000';
const CELL = '\uE001';

/** Anything already carrying a sentinel is not doing it on purpose. Stripped before use. */
const SENTINELS = /[\uE000\uE001]/g;

/**
 * The invisible characters a preheader block is padded with.
 *
 * Every well-built template ends its hidden preheader with a long run of zero-width spaces and
 * combining joiners. They stop an inbox continuing from the preheader straight into the first
 * words of the body, and in the HTML they do exactly the right thing. Dragged into a text part
 * they are thirty invisible characters printed after the preheader, which renders as a ragged
 * trail of gaps and is the first thing anybody reading the plain-text half sees.
 *
 * Stripped after decoding, because they arrive as numeric entities rather than as characters.
 */
const INVISIBLES = /\u200B|\u200C|\u200D|\u2060|\uFEFF|\u034F|\u00AD/g;

/** Tags whose *contents* are not content: they never appear in the text part. */
const DROPPED_BLOCKS = /<(script|style|head|title|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

/**
 * An unclosed `<style>` or `<script>`, left behind by a document truncated mid-tag. Everything
 * after it is stylesheet or program text, never message text, so it goes with the tag.
 */
const UNCLOSED_CODE = /<(script|style)\b[^>]*>[\s\S]*$/i;

/** A stray `<head>`, `<title>` or `<noscript>` tag. Only the tag goes: the rest may be content. */
const STRAY_OPENERS = /<\/?(head|title|noscript)\b[^>]*>/gi;

const COMMENTS = /<!--[\s\S]*?-->/g;

const LINE_BREAKS = /<br\s*\/?>/gi;

/** Boundaries that read as a blank line: paragraphs, headings, whole tables and lists. */
const PARA_ENDS = /<\/(p|h[1-6]|table|ul|ol|blockquote)\s*>/gi;
const PARA_STARTS = /<(p|h[1-6]|blockquote)\b[^>]*>/gi;

/** Boundaries that read as a single line break: rows, list items, generic blocks. */
const LINE_ENDS = /<\/(div|tr|li|section|article|header|footer)\s*>/gi;
const LINE_STARTS = /<(div|tr|table|section|article|header|footer)\b[^>]*>/gi;

const LIST_ITEMS = /<li\b[^>]*>/gi;

/** A table cell boundary is a gap, not a line break: a receipt row reads label then value. */
const CELL_ENDS = /<\/t[dh]\s*>/gi;

const ANCHORS =
  /<a\b[^>]*?href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))[^>]*>([\s\S]*?)<\/a\s*>/gi;

const IMAGES = /<img\b[^>]*>/gi;

const ALT_ATTRIBUTE = /\balt\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i;

/**
 * Anything that is actually a tag.
 *
 * Deliberately narrower than `<[^>]*>`: a tag name starts with a letter, optionally behind a
 * slash. The loose form eats `a < b and c > d`, which is prose, not markup, and prose quietly
 * going missing out of a receipt is the failure this whole module is here to avoid.
 */
const ANY_TAG = /<\/?[a-zA-Z][^>]*>/g;

/**
 * The doctype, and any processing instruction.
 *
 * Its own pattern because `ANY_TAG` above deliberately requires a letter after the `<`, and
 * `<!doctype html>` has a `!`. Narrowing the tag pattern to protect prose left the doctype
 * standing, and every seeded template is a complete document: the text part of all twelve
 * opened with the literal words "<!doctype html>". A `!` or `?` immediately after the `<` is
 * never prose, so this cannot take a sentence with it.
 */
const DOCTYPE_OR_PI = /<[!?][^>]*>/g;

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  mdash: '—',
  ndash: '–',
  hellip: '…',
  copy: '©',
  reg: '®',
  trade: '™',
  rsquo: '’',
  lsquo: '‘',
  ldquo: '“',
  rdquo: '”',
  middot: '·',
  bull: '•',
  deg: '°',
  euro: '€',
  pound: '£',
  laquo: '«',
  raquo: '»',
};

/**
 * Decode the entity set a mail template actually contains.
 *
 * `&amp;` is decoded **last** and on its own pass. Decoding it with the others turns
 * `&amp;lt;`, which is how a template writes a literal `&lt;`, into `<`, silently inventing
 * markup in the text part that the HTML part never had.
 */
function decodeEntities(input: string): string {
  const named = input.replace(/&([a-zA-Z][a-zA-Z0-9]{1,9});/g, (whole, name: string) => {
    const value = NAMED_ENTITIES[name.toLowerCase()];
    return value ?? whole;
  });

  const numeric = named.replace(/&#(x?)([0-9a-fA-F]{1,6});/g, (whole, hex: string, digits: string) => {
    const code = Number.parseInt(digits, hex === '' ? 10 : 16);
    // Surrogates and out-of-range values are not characters; leaving the entity visible is
    // better than emitting a replacement glyph nobody can explain.
    if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return whole;
    if (code >= 0xd800 && code <= 0xdfff) return whole;
    try {
      return String.fromCodePoint(code);
    } catch {
      return whole;
    }
  });

  return numeric.replace(/&amp;/gi, '&');
}

/** Strip tags and decode entities from a fragment that is already known to be inline. */
function flattenInline(fragment: string): string {
  return decodeEntities(fragment.replace(DOCTYPE_OR_PI, ' ').replace(ANY_TAG, ' '))
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Two destinations are "the same" when only the scheme or a trailing slash separates them, so
 * `<a href="mailto:help@x.test">help@x.test</a>` reads as one address rather than twice.
 */
function sameDestination(label: string, href: string): boolean {
  const strip = (value: string): string =>
    value
      .trim()
      .toLowerCase()
      .replace(/^mailto:/, '')
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '');
  return strip(label) === strip(href);
}

/**
 * The `text/plain` alternative for a rendered email.
 *
 * Pure: no DOM, no network, no evaluation. Safe to call on every keystroke and on HTML the
 * console did not author.
 */
export function htmlToPlainText(html: string): string {
  if (html === '') return '';

  let text = html.replace(SENTINELS, '').replace(/\r\n?/g, '\n');

  text = text.replace(COMMENTS, ' ');
  text = text.replace(DROPPED_BLOCKS, ' ');
  text = text.replace(UNCLOSED_CODE, ' ');
  text = text.replace(STRAY_OPENERS, ' ');

  // Links first, while the anchor still has both its href and its label. The destination goes in
  // parentheses rather than angle brackets: angle brackets are exactly what the tag stripper
  // below is looking for, and the version that used them lost every URL it had just recovered.
  text = text.replace(
    ANCHORS,
    (_whole, dq: string | undefined, sq: string | undefined, bare: string | undefined, inner: string) => {
      const href = (dq ?? sq ?? bare ?? '').trim();
      const label = flattenInline(inner);
      if (href === '' || href.startsWith('#')) return label;
      if (label === '') return href;
      if (sameDestination(label, href)) return label;
      return `${label} (${href})`;
    },
  );

  // Images become their alt text, or nothing. A spacer GIF has no alt and no business here.
  text = text.replace(IMAGES, (tag: string) => {
    const match = ALT_ATTRIBUTE.exec(tag);
    const alt = (match?.[1] ?? match?.[2] ?? match?.[3] ?? '').trim();
    return alt === '' ? ' ' : ` [${alt}] `;
  });

  text = text.replace(LINE_BREAKS, '\n');
  text = text.replace(CELL_ENDS, CELL);
  text = text.replace(LIST_ITEMS, '\n- ');
  text = text.replace(PARA_ENDS, PARA);
  text = text.replace(PARA_STARTS, PARA);
  text = text.replace(LINE_ENDS, '\n');
  text = text.replace(LINE_STARTS, '\n');

  text = text.replace(DOCTYPE_OR_PI, ' ');
  text = text.replace(ANY_TAG, ' ');
  text = decodeEntities(text);
  text = text.replace(INVISIBLES, '');

  // ── Collapse ──
  // Horizontal runs first, then the whitespace hugging a boundary, then the boundaries
  // themselves. In that order: trimming before collapsing leaves lines that are only whitespace
  // behind, and collapsing boundaries before stripping the spaces between them leaves the runs
  // broken into pieces that no longer look like runs.
  text = text.replace(/[^\S\n]+/g, ' ');
  // The cell sentinel is in this class too. Without it the space left behind by the stripped
  // `<td>` opening tag survives beside the gap, and a receipt row reads with three spaces in it.
  text = text.replace(/ *([\n\uE000\uE001]) */g, '$1');
  // A cell gap at the start or the end of a line is the edge of the row, not a gap inside it.
  text = text.replace(/\uE001+(?=[\n\uE000]|$)/g, '');
  text = text.replace(/(^|[\n\uE000])\uE001+/g, '$1');
  text = text.replace(/[\n\uE000]+/g, (run) => (run.includes(PARA) ? '\n\n' : '\n'));
  text = text.replace(/\uE001+/g, '  ');

  return text.trim();
}

/**
 * Where Gmail stops rendering a message and shows "[Message clipped]" with a link.
 *
 * Measured on the raw markup, not on the words, so a template that is 4 kB of text inside 110 kB
 * of nested tables clips exactly as hard as one that is 110 kB of prose. Worth surfacing next to
 * the editor because the failure is invisible to whoever wrote it: their own test send renders
 * fine right up until the footer, which is where the unsubscribe link lives.
 */
export const GMAIL_CLIP_BYTES = 102 * 1024;

/**
 * The byte length of the markup as a mail server counts it.
 *
 * UTF-8 bytes rather than JavaScript characters, because the naira sign, a long dash and an emoji
 * are one character each and three or four bytes each, and the limit that matters is in bytes.
 */
export function markupByteLength(html: string): number {
  return new TextEncoder().encode(html).length;
}

/**
 * A one-line summary of a message, for a list row or an inbox-style preview.
 *
 * The preheader is the first readable sentence of the text part, which is exactly what an inbox
 * shows beside the subject, so it is derived the same way rather than guessed at separately.
 */
export function previewLine(html: string, maxChars = 140): string {
  const flat = htmlToPlainText(html).replace(/\s+/g, ' ').trim();
  if (flat.length <= maxChars) return flat;
  // Break on a word boundary when there is one near the end, so the ellipsis does not land
  // mid-word on a long reference or an address.
  const clipped = flat.slice(0, maxChars);
  const lastSpace = clipped.lastIndexOf(' ');
  const body = lastSpace > maxChars - 24 ? clipped.slice(0, lastSpace) : clipped;
  return `${body.trimEnd()}...`;
}
